import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Check, Trash2, Loader2, Film } from "lucide-react";
import { useLang } from "@/i18n";
import { mediaUrl, uploadMedia, listMedia, deleteMedia } from "@/lib/media";

export default function MediaPicker({ selectable = false, selected = [], onChange, onDelete }) {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try { setItems(await listMedia()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        try { const m = await uploadMedia(f); setItems((prev) => [m, ...prev]); }
        catch (err) {
          const d = err.response?.data?.detail;
          toast.error(d === "FILE_TOO_LARGE" ? "Fichier trop lourd (max 30 Mo)" :
            d === "UNSUPPORTED_FILE" ? "Format non supporté" : t("err_generic"));
        }
      }
    } finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const toggle = (id) => {
    if (!selectable) return;
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    await deleteMedia(id);
    setItems(items.filter((i) => i.id !== id));
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    onDelete && onDelete(id);
  };

  return (
    <div>
      <input ref={inputRef} type="file" accept="image/*,video/*" multiple onChange={onFiles} className="hidden" data-testid="media-file-input" />
      <button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="media-upload-btn"
        className="w-full rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors p-8 flex flex-col items-center gap-2 mb-5">
        {uploading ? <Loader2 className="animate-spin text-primary" size={30} /> : <Upload className="text-primary" size={30} />}
        <span className="font-bold text-primary">{uploading ? t("media_uploading") : t("media_add")}</span>
      </button>

      {loading ? (
        <div className="text-center text-stone-400 py-6 animate-pulse">{t("loading")}</div>
      ) : items.length === 0 ? (
        <div className="text-center text-stone-500 py-6">{t("media_none")}</div>
      ) : (
        <>
          {selectable && <p className="text-sm text-stone-500 mb-3">{t("media_pick_hint")}</p>}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="media-grid">
            {items.map((m) => {
              const isSel = selected.includes(m.id);
              return (
                <div key={m.id} onClick={() => toggle(m.id)} data-testid={`media-item-${m.id}`}
                  className={`relative aspect-square rounded-2xl overflow-hidden border-2 group ${selectable ? "cursor-pointer" : ""} ${isSel ? "border-primary ring-4 ring-primary/30" : "border-stone-200"}`}>
                  {m.kind === "video" ? (
                    <video src={mediaUrl(m.id)} className="w-full h-full object-cover" muted playsInline />
                  ) : (
                    <img src={mediaUrl(m.id)} alt={m.original_filename} className="w-full h-full object-cover" />
                  )}
                  {m.kind === "video" && <Film className="absolute top-2 left-2 text-white drop-shadow" size={18} />}
                  {selectable && isSel && (
                    <div className="absolute inset-0 bg-primary/30 grid place-items-center">
                      <span className="w-8 h-8 rounded-full bg-primary text-white grid place-items-center"><Check size={18} /></span>
                    </div>
                  )}
                  <button onClick={(e) => remove(m.id, e)} data-testid={`media-delete-${m.id}`}
                    className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/90 text-red-500 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
